import * as THREE from 'three';
import { CLASSICAL } from '../materials';
import { Pole, pyramid } from '../parts';

/**
 * Pieces the city stages are assembled from. Everything stays inside the hex
 * wall (`WALL_RADIUS`); the city component scales the whole city by CITY_SCALE.
 */

const { DARK_WOOD, STONE, PLASTER, TERRACOTTA, WALL_STONE } = CLASSICAL;

const G = {
  keep: new THREE.BoxGeometry(0.28, 0.42, 0.28),
  keepRoof: pyramid(0.25, 0.22),
  bigKeep: new THREE.BoxGeometry(0.34, 0.4, 0.34),
  keepTier: new THREE.BoxGeometry(0.22, 0.2, 0.22),
  tierRoof: pyramid(0.2, 0.2),
  flag: new THREE.BoxGeometry(0.14, 0.08, 0.01),
  house: new THREE.BoxGeometry(0.17, 0.13, 0.15),
  houseRoof: pyramid(0.15, 0.1),
  wallSegment: new THREE.BoxGeometry(1, 0.1, 0.045),
  wallTower: new THREE.CylinderGeometry(0.045, 0.05, 0.15, 6),
  tallTower: new THREE.CylinderGeometry(0.05, 0.055, 0.26, 6),
  towerCap: new THREE.ConeGeometry(0.065, 0.09, 6),
  banner: new THREE.BoxGeometry(0.05, 0.1, 0.01),
  gateBlock: new THREE.BoxGeometry(0.2, 0.2, 0.1),
  gateDoor: new THREE.BoxGeometry(0.08, 0.12, 0.105),
  gateTower: new THREE.CylinderGeometry(0.045, 0.05, 0.28, 6),
  gateRoof: pyramid(0.09, 0.08),
};

// Hex wall: one solid segment per side plus a small tower on each corner.
// Corners follow the pointy-top tiles (first vertex on +z).
export const WALL_RADIUS = 0.66;
export const WALL_CORNERS = Array.from({ length: 6 }, (_, i) => {
  const a = (i * Math.PI) / 3;
  return { a, x: WALL_RADIUS * Math.sin(a), z: WALL_RADIUS * Math.cos(a) };
});
export const WALL_SEGMENTS = WALL_CORNERS.map((from, i) => {
  const to = WALL_CORNERS[(i + 1) % 6];
  return {
    x: (from.x + to.x) / 2,
    z: (from.z + to.z) / 2,
    // Box length runs along local x; turn it onto the edge direction.
    yaw: Math.atan2(-(to.z - from.z), to.x - from.x),
  };
});

/** The hex wall; `tall` swaps the corner towers for taller, capped ones. */
export function Wall({ tall = false }: { tall?: boolean }) {
  return (
    <>
      {WALL_SEGMENTS.map((seg) => (
        <mesh
          key={seg.yaw}
          geometry={G.wallSegment}
          material={WALL_STONE}
          position={[seg.x, 0.05, seg.z]}
          rotation={[0, seg.yaw, 0]}
          scale={[WALL_RADIUS, 1, 1]}
        />
      ))}
      {WALL_CORNERS.map((c) =>
        tall ? (
          <group key={`${c.x}:${c.z}`} position={[c.x, 0, c.z]}>
            <mesh geometry={G.tallTower} material={WALL_STONE} position={[0, 0.13, 0]} />
            <mesh geometry={G.towerCap} material={TERRACOTTA} position={[0, 0.305, 0]} />
          </group>
        ) : (
          <mesh
            key={`${c.x}:${c.z}`}
            geometry={G.wallTower}
            material={WALL_STONE}
            position={[c.x, 0.075, c.z]}
          />
        )
      )}
    </>
  );
}

/** Team-coloured banners hung on the outer face of each (tall) corner tower. */
export function TowerBanners({ accent }: { accent: THREE.Material }) {
  const r = WALL_RADIUS + 0.056;
  return (
    <>
      {WALL_CORNERS.map((c) => (
        <mesh
          key={c.a}
          geometry={G.banner}
          material={accent}
          position={[r * Math.sin(c.a), 0.17, r * Math.cos(c.a)]}
          rotation={[0, c.a, 0]}
        />
      ))}
    </>
  );
}

/**
 * A gatehouse over the wall side facing +z and the camera (the first segment,
 * front-right): a stone block with a gate, flanked by towers with team roofs.
 */
export function Gatehouse({ accent }: { accent: THREE.Material }) {
  const seg = WALL_SEGMENTS[0];
  return (
    <group position={[seg.x, 0, seg.z]} rotation={[0, seg.yaw, 0]}>
      <mesh geometry={G.gateBlock} material={WALL_STONE} position={[0, 0.1, 0]} />
      <mesh geometry={G.gateDoor} material={DARK_WOOD} position={[0, 0.06, 0]} />
      {[-0.12, 0.12].map((x) => (
        <group key={x} position={[x, 0, 0]}>
          <mesh geometry={G.gateTower} material={WALL_STONE} position={[0, 0.14, 0]} />
          <mesh geometry={G.gateRoof} material={accent} position={[0, 0.32, 0]} />
        </group>
      ))}
    </group>
  );
}

/** The central keep with its team roof and flag; `big` adds a second tier. */
export function Keep({ accent, big = false }: { accent: THREE.Material; big?: boolean }) {
  if (!big) {
    return (
      <>
        <mesh geometry={G.keep} material={STONE} position={[0, 0.21, 0]} />
        <mesh geometry={G.keepRoof} material={accent} position={[0, 0.53, 0]} />
        <Pole length={0.26} position={[0, 0.74, 0]} material={DARK_WOOD} />
        <mesh geometry={G.flag} material={accent} position={[0.07, 0.82, 0]} />
      </>
    );
  }
  return (
    <>
      <mesh geometry={G.bigKeep} material={STONE} position={[0, 0.2, 0]} />
      <mesh geometry={G.keepTier} material={STONE} position={[0, 0.5, 0]} />
      <mesh geometry={G.tierRoof} material={accent} position={[0, 0.7, 0]} />
      <Pole length={0.26} position={[0, 0.9, 0]} material={DARK_WOOD} />
      <mesh geometry={G.flag} material={accent} position={[0.07, 0.98, 0]} />
    </>
  );
}

/** Angles (radians from +x) of the houses ringing the keep. */
export const HOUSE_ANGLES = [0.3, 1.5, 2.6, 3.7, 4.9];
/** Two more houses in the widest gaps (kept clear of the gatehouse). */
export const MORE_HOUSE_ANGLES = [...HOUSE_ANGLES, 2.05, 5.75];
const HOUSE_RING = 0.43;

export function Houses({
  angles,
  material = PLASTER,
}: {
  angles: number[];
  material?: THREE.Material;
}) {
  return (
    <>
      {angles.map((a) => {
        const x = Math.cos(a) * HOUSE_RING;
        const z = Math.sin(a) * HOUSE_RING;
        return (
          <group key={a} position={[x, 0, z]} rotation={[0, -a, 0]}>
            <mesh geometry={G.house} material={material} position={[0, 0.065, 0]} />
            <mesh geometry={G.houseRoof} material={TERRACOTTA} position={[0, 0.18, 0]} />
          </group>
        );
      })}
    </>
  );
}
